import React, { useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Trophy, Users, User, CalendarDays, BarChart2, Loader2, Lightbulb, TrendingUp, Handshake, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import useStore from '@/store/useStore';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/components/ui/use-toast';
import { motion, useInView } from 'framer-motion';

const HomePage = () => {
  const {
    tournaments,
    teams,
    players,
    matches,
    fetchTournaments,
    fetchTeams,
    fetchPlayers,
    fetchMatches,
    loading
  } = useStore();

  const { user } = useAuth();

  const whatIsThisRef = useRef(null);
  const howItWorksRef = useRef(null);
  const whyChooseUsRef = useRef(null);

  const whatIsThisInView = useInView(whatIsThisRef, { once: true, amount: 0.1 });
  const howItWorksInView = useInView(howItWorksRef, { once: true, amount: 0.1 });
  const whyChooseUsInView = useInView(whyChooseUsRef, { once: true, amount: 0.1 });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.1 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
  };

  useEffect(() => {
    // Only fetch data if user is logged in
    if (user) {
      fetchTournaments();
      fetchTeams();
      fetchPlayers();
      fetchMatches();
    }
  }, [user, fetchTournaments, fetchTeams, fetchPlayers, fetchMatches]);

  const stats = [
    {
      title: 'Tournaments',
      value: tournaments.length,
      icon: Trophy,
      color: 'bg-cricket-green text-white',
      path: '/tournaments'
    },
    {
      title: 'Teams',
      value: teams.length,
      icon: Users,
      color: 'bg-cricket-green-light text-white',
      path: '/teams'
    },
    {
      title: 'Players',
      value: players.length,
      icon: User,
      color: 'bg-cricket-boundary text-white',
      path: '/players'
    },
    {
      title: 'Matches',
      value: matches.length,
      icon: CalendarDays,
      color: 'bg-cricket-accent text-white',
      path: '/matches'
    }
  ];

  if (loading.tournaments || loading.teams || loading.matches || loading.players) {
    return <div className="flex items-center justify-center h-[70vh]">
      <Loader2 className="h-8 w-8 animate-spin" />
    </div>;
  }

  if (!user) {
    return (
      <div className="space-y-24 animate-fade-in mb-12">
        {/* Hero Section */}
        <section className="relative overflow-hidden py-12 sm:py-24 md:py-32 bg-gradient-to-br from-primary to-green-700 text-primary-foreground">
          <div className="absolute inset-0 z-0 opacity-10">
            {/* Background pattern/texture */}
            <svg className="w-full h-full" fill="none" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice">
              <pattern id="pattern-circles" x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
                <circle cx="5" cy="5" r="1" fill="currentColor" />
              </pattern>
              <rect x="0" y="0" width="100%" height="100%" fill="url(#pattern-circles)" />
            </svg>
          </div>
          <div className="container mx-auto px-4 text-center relative z-10">
            <motion.h1 
              initial={{ opacity: 0, y: -50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="text-3xl sm:text-5xl md:text-7xl font-extrabold mb-4 sm:mb-6 drop-shadow-lg"
            >
              Cricket Scoring System
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: -50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
              className="text-lg sm:text-xl md:text-2xl max-w-4xl mx-auto mb-6 sm:mb-10 opacity-90 leading-relaxed px-4"
            >
              A modern, intuitive platform for managing cricket tournaments, teams, and matches with live updates and in-depth statistics. 
              Elevate your cricket events, from local leagues to professional championships.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: "easeOut", delay: 0.4 }}
            >
              <Link to="/auth">
                <Button className="bg-accent text-accent-foreground px-6 sm:px-10 py-3 sm:py-5 text-lg sm:text-xl md:text-2xl rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                  Get Started Now
                </Button>
              </Link>
              <p className="text-xs sm:text-sm opacity-80 mt-3 sm:mt-4">Sign in or create an account to unlock full features.</p>
            </motion.div>
          </div>
        </section>

        {/* What is this? */}
        <motion.section 
          ref={whatIsThisRef}
          initial="hidden"
          animate={whatIsThisInView ? "visible" : "hidden"}
          variants={containerVariants}
          className="container mx-auto px-4 py-16 text-center"
          id="what-is-this"
        >
          <motion.h2 variants={itemVariants} className="text-4xl md:text-5xl font-bold mb-12 text-foreground relative before:absolute before:w-20 before:h-1 before:bg-primary before:bottom-[-10px] before:left-1/2 before:-translate-x-1/2">
            What is Cricket Scoring System?
          </motion.h2>
          <motion.p variants={itemVariants} className="text-lg md:text-xl text-muted-foreground max-w-4xl mx-auto mb-16 leading-relaxed">
            Cricket Scoring System is a comprehensive digital platform designed to simplify the scoring and management of cricket matches and tournaments. 
            From real-time score updates to detailed player statistics and tournament overviews, our system provides everything you need to run a smooth and engaging cricket event.
          </motion.p>
          <motion.div variants={containerVariants} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            <motion.div variants={itemVariants}>
              <Card className="bg-card shadow-lg p-8 text-left h-full flex flex-col justify-between hover:shadow-xl transition-shadow duration-300">
                <CardHeader className="pb-4">
                  <Lightbulb className="h-10 w-10 text-primary mb-4" />
                  <CardTitle className="text-2xl font-bold text-foreground">Real-time Scoring</CardTitle>
                </CardHeader>
                <CardContent className="flex-grow">
                  <p className="text-muted-foreground text-base">Update scores ball-by-ball with an intuitive interface, ensuring fans and officials are always up-to-date.</p>
                </CardContent>
              </Card>
            </motion.div>
            <motion.div variants={itemVariants}>
              <Card className="bg-card shadow-lg p-8 text-left h-full flex flex-col justify-between hover:shadow-xl transition-shadow duration-300">
                <CardHeader className="pb-4">
                  <BarChart2 className="h-10 w-10 text-primary mb-4" />
                  <CardTitle className="text-2xl font-bold text-foreground">Detailed Statistics</CardTitle>
                </CardHeader>
                <CardContent className="flex-grow">
                  <p className="text-muted-foreground text-base">Access comprehensive statistics for players, teams, and tournaments, helping you analyze performance and track progress.</p>
                </CardContent>
              </Card>
            </motion.div>
            <motion.div variants={itemVariants}>
              <Card className="bg-card shadow-lg p-8 text-left h-full flex flex-col justify-between hover:shadow-xl transition-shadow duration-300">
                <CardHeader className="pb-4">
                  <Trophy className="h-10 w-10 text-primary mb-4" />
                  <CardTitle className="text-2xl font-bold text-foreground">Tournament Management</CardTitle>
                </CardHeader>
                <CardContent className="flex-grow">
                  <p className="text-muted-foreground text-base">Effortlessly set up and manage tournaments, create schedules, and track standings with ease.</p>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        </motion.section>

        {/* How it Works */}
        <motion.section 
          ref={howItWorksRef}
          initial="hidden"
          animate={howItWorksInView ? "visible" : "hidden"}
          variants={containerVariants}
          className="container mx-auto px-4 py-16 text-center bg-gradient-to-r from-muted/30 to-background rounded-lg shadow-inner"
          id="how-it-works"
        >
          <motion.h2 variants={itemVariants} className="text-3xl sm:text-4xl md:text-5xl font-bold mb-8 sm:mb-12 text-foreground relative before:absolute before:w-20 before:h-1 before:bg-primary before:bottom-[-10px] before:left-1/2 before:-translate-x-1/2">
            How It Works
          </motion.h2>
          <motion.div variants={containerVariants} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-12 items-start">
            <motion.div variants={itemVariants} className="space-y-4 text-left p-4 sm:p-6 bg-card rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-primary text-primary-foreground text-xl sm:text-2xl font-bold mx-auto md:mx-0 shadow-lg">
                1
              </div>
              <h3 className="text-xl sm:text-2xl font-semibold text-foreground mt-4">Set Up Your Event</h3>
              <p className="text-sm sm:text-base text-muted-foreground">Create tournaments, add teams and players, and schedule matches in just a few clicks. Our intuitive interface guides you through the process.</p>
            </motion.div>
            <motion.div variants={itemVariants} className="space-y-4 text-left p-4 sm:p-6 bg-card rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-primary text-primary-foreground text-xl sm:text-2xl font-bold mx-auto md:mx-0 shadow-lg">
                2
              </div>
              <h3 className="text-xl sm:text-2xl font-semibold text-foreground mt-4">Live Scoring</h3>
              <p className="text-sm sm:text-base text-muted-foreground">Use our dedicated scoring interface to record every ball, run, and wicket in real-time. The scoreboard updates instantly for everyone following the match.</p>
            </motion.div>
            <motion.div variants={itemVariants} className="space-y-4 text-left p-4 sm:p-6 bg-card rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-primary text-primary-foreground text-xl sm:text-2xl font-bold mx-auto md:mx-0 shadow-lg">
                3
              </div>
              <h3 className="text-xl sm:text-2xl font-semibold text-foreground mt-4">Analyze & Share</h3>
              <p className="text-sm sm:text-base text-muted-foreground">After matches, dive deep into player performance, team statistics, and tournament standings. Share results easily with your community.</p>
            </motion.div>
          </motion.div>
        </motion.section>

        {/* Why Choose Us & Benefits */}
        <motion.section 
          ref={whyChooseUsRef}
          initial="hidden"
          animate={whyChooseUsInView ? "visible" : "hidden"}
          variants={containerVariants}
          className="container mx-auto px-4 py-12 sm:py-16"
          id="why-choose-us"
        >
          <motion.h2 variants={itemVariants} className="text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-8 sm:mb-12 text-foreground relative before:absolute before:w-20 before:h-1 before:bg-primary before:bottom-[-10px] before:left-1/2 before:-translate-x-1/2">
            Why Choose Cricket Scoring System?
          </motion.h2>
          <motion.div variants={containerVariants} className="grid grid-cols-1 lg:grid-cols-2 gap-12 sm:gap-16 items-center">
            <div className="space-y-8 sm:space-y-10">
              <motion.div variants={itemVariants} className="flex items-start space-x-3 sm:space-x-4">
                <Handshake className="h-7 w-7 sm:h-9 sm:w-9 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl sm:text-2xl font-semibold text-foreground">Ease of Use</h3>
                  <p className="text-sm sm:text-base text-muted-foreground">Our platform is designed with user-friendliness in mind, making it accessible for everyone from casual fans to professional organizers.</p>
                </div>
              </motion.div>
              <motion.div variants={itemVariants} className="flex items-start space-x-3 sm:space-x-4">
                <TrendingUp className="h-7 w-7 sm:h-9 sm:w-9 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl sm:text-2xl font-semibold text-foreground">Unparalleled Accuracy</h3>
                  <p className="text-sm sm:text-base text-muted-foreground">Minimize errors with our precise scoring mechanisms and robust data validation, ensuring every stat is correct.</p>
                </div>
              </motion.div>
              <motion.div variants={itemVariants} className="flex items-start space-x-3 sm:space-x-4">
                <CheckCircle className="h-7 w-7 sm:h-9 sm:w-9 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl sm:text-2xl font-semibold text-foreground">Comprehensive Features</h3>
                  <p className="text-sm sm:text-base text-muted-foreground">From detailed scorecards to insightful analytics, we offer a complete suite of tools for all your cricket scoring needs.</p>
                </div>
              </motion.div>
            </div>

            <div className="space-y-6 sm:space-y-8 p-4 sm:p-6 bg-muted/20 rounded-lg shadow-inner">
              <motion.h3 variants={itemVariants} className="text-2xl sm:text-3xl font-bold text-foreground mb-4 sm:mb-6">Benefits You'll Love:</motion.h3>
              <ul className="space-y-3 sm:space-y-4 text-muted-foreground text-base sm:text-lg">
                <motion.li variants={itemVariants} className="flex items-start"><CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-green-500 mr-2 sm:mr-3 flex-shrink-0" /> Save time and effort on manual scoring.</motion.li>
                <motion.li variants={itemVariants} className="flex items-start"><CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-green-500 mr-2 sm:mr-3 flex-shrink-0" /> Enhance spectator engagement with live updates.</motion.li>
                <motion.li variants={itemVariants} className="flex items-start"><CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-green-500 mr-2 sm:mr-3 flex-shrink-0" /> Gain valuable insights from detailed statistics.</motion.li>
                <motion.li variants={itemVariants} className="flex items-start"><CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-green-500 mr-2 sm:mr-3 flex-shrink-0" /> Improve tournament organization and fairness.</motion.li>
                <motion.li variants={itemVariants} className="flex items-start"><CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-green-500 mr-2 sm:mr-3 flex-shrink-0" /> Access your data anytime, anywhere.</motion.li>
              </ul>
            </div>
          </motion.div>
        </motion.section>

      <div className="text-center mt-6 sm:mt-8">
        <Link to="/auth">
          <Button className="bg-cricket-green text-white px-4 sm:px-6 py-4 sm:py-6 text-lg sm:text-xl">
            Get Started
          </Button>
        </Link>
        <p className="text-sm sm:text-base text-gray-500 mt-3 sm:mt-4">Sign in or create an account to start managing cricket tournaments</p>
      </div>
      </div>
    );
  }

  return <div className="space-y-6 sm:space-y-8 animate-fade-in">
    <div className="text-center mb-8 sm:mb-12">
      <h1 className="text-2xl sm:text-4xl font-bold mb-3 sm:mb-4">Cricket Scoring System</h1>
      <p className="text-base sm:text-xl text-gray-600 max-w-2xl mx-auto px-4">
        A modern cricket scoring application for tournaments, teams, and matches with live updates and statistics.
      </p>
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
      {stats.map((stat, index) => (
        <Link to={stat.path} key={index}>
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader className={`${stat.color} rounded-t-lg`}>
              <div className="flex justify-between items-center">
                <CardTitle className="text-sm sm:text-lg">{stat.title}</CardTitle>
                <stat.icon size={20} className="sm:w-6 sm:h-6" />
              </div>
            </CardHeader>
            <CardContent className="pt-4 sm:pt-6 text-center">
              <p className="text-2xl sm:text-4xl font-bold">{stat.value}</p>
            </CardContent>
          </Card>
        </Link>
      ))}
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 mt-8 sm:mt-12">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg sm:text-xl">Live Matches</CardTitle>
          <CardDescription className="text-sm sm:text-base">Currently ongoing cricket matches</CardDescription>
        </CardHeader>
        <CardContent>
          {matches.filter(m => m.status === 'live').length > 0 ? <div className="space-y-3 sm:space-y-4">
            {matches.filter(m => m.status === 'live').map(match => {
              const team1 = teams.find(t => t.id === match.team1Id);
              const team2 = teams.find(t => t.id === match.team2Id);
              return <div key={match.id} className="p-3 sm:p-4 border rounded-lg hover:bg-accent/50">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0">
                  <div className="flex items-center gap-2">
                    <span className="relative flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                    </span>
                    <h4 className="font-semibold text-sm sm:text-base">{team1?.name || 'Team 1'} vs {team2?.name || 'Team 2'}</h4>
                  </div>
                  <Link to={`/matches/${match.id}`}>
                    <Button variant="outline" size="sm" className="text-xs sm:text-sm">View Match</Button>
                  </Link>
                </div>
                <div className="mt-2 flex flex-col sm:flex-row justify-between gap-1 sm:gap-0">
                  <span className="text-xs sm:text-sm font-medium">
                    {team1?.name || 'Team 1'}: {match.team1Score.runs}/{match.team1Score.wickets} ({match.team1Score.overs}.{match.team1Score.balls})
                  </span>
                  <span className="text-xs bg-red-100 text-red-800 py-0.5 px-2 rounded-full self-start">LIVE</span>
                </div>
              </div>;
            })}
          </div> : <div className="text-center py-6 sm:py-8 text-gray-500 text-sm sm:text-base">
            No live matches at the moment
          </div>}
          <div className="mt-4">
            <Link to="/matches">
              <Button variant="outline" className="w-full text-sm sm:text-base">View All Matches</Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg sm:text-xl">Quick Actions</CardTitle>
          <CardDescription className="text-sm sm:text-base">Manage tournaments, teams, players and matches</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 sm:space-y-4">
          <Link to="/tournaments/new" className="block">
            <Button className="w-full bg-cricket-green hover:bg-cricket-green-light text-sm sm:text-base">Create Tournament</Button>
          </Link>
          <Link to="/teams/new" className="block" onClick={(e) => {
            if (tournaments.length === 0) {
              e.preventDefault();
              toast({
                title: "Cannot create teams yet",
                description: "Please create a tournament first.",
                variant: "destructive",
              });
            }
          }}>
            <Button className="w-full bg-cricket-green-light hover:bg-cricket-green text-sm sm:text-base">Add Team</Button>
          </Link>
          <Link to="/players/new" className="block" onClick={(e) => {
            if (teams.length === 0) {
              e.preventDefault();
              toast({
                title: "Cannot create players yet",
                description: "Please create a team first.",
                variant: "destructive",
              });
            }
          }}>
            <Button className="w-full bg-cricket-boundary hover:opacity-90 text-sm sm:text-base">Add Player</Button>
          </Link>
          <Link to="/matches/new" className="block" onClick={(e) => {
            if (teams.length < 2) {
              e.preventDefault();
              toast({
                title: "Cannot schedule matches yet",
                description: "Please create at least two teams first.",
                variant: "destructive",
              });
            }
          }}>
            <Button className="w-full bg-cricket-accent hover:opacity-90 text-sm sm:text-base">Schedule Match</Button>
          </Link>
          <Link to="/statistics" className="block">
            <Button variant="outline" className="w-full text-sm sm:text-base">View Statistics</Button>
          </Link>
        </CardContent>
      </Card>

    </div>
  </div>;
};

export default HomePage;
